import { create } from "zustand";
import { scopedSetter } from "../api/scopedSetter.js";
import { t } from "../i18n/index.js";
import { toast } from "../ui/Toast.js";
import { errorText } from "../base/state.js";
import { useUnits } from "../units/state.js";
import { useSession } from "../state/session.js";
import { allianceApi } from "./api.js";
import type { AllianceBuildingView, AllianceDuel, AllianceGifts, AllianceHelp, AllianceListEntry, AllianceLogEntry, AllianceRequest, AllianceView, CreateAllianceInput, DiplomacyOverview, ProposalKind } from "./types.js";

export type AllianceTab = "overview" | "members" | "contribute" | "buildings" | "shop" | "gifts" | "diplomacy" | "events" | "requests" | "log";
export const ALLIANCE_TABS: AllianceTab[] = ["overview", "members", "contribute", "buildings", "shop", "gifts", "diplomacy", "events", "requests", "log"];

interface AllianceStore {
  /** null = no alliance; undefined = not loaded yet. */
  alliance: AllianceView | null | undefined;
  list: AllianceListEntry[] | null;
  requests: { applications: AllianceRequest[]; invites: AllianceRequest[] } | null;
  help: AllianceHelp[] | null;
  log: AllianceLogEntry[] | null;
  buildings: AllianceBuildingView[] | null;
  gifts: AllianceGifts | null;
  /** Relations, proposals and history (D73); null until loaded or without an alliance. */
  diplomacy: DiplomacyOverview | null;
  /** The alliance duel of the running alliance event (D74); null without one. */
  duel: AllianceDuel | null;
  loading: boolean;
  refresh(): Promise<void>;
  search(query: string): Promise<void>;
  refreshRequests(): Promise<void>;
  refreshHelp(): Promise<void>;
  refreshLog(): Promise<void>;
  create(input: CreateAllianceInput): Promise<boolean>;
  join(id: string, message?: string): Promise<boolean>;
  leave(): Promise<void>;
  helpAll(): Promise<number>;
  refreshBuildings(): Promise<void>;
  refreshGifts(): Promise<void>;
  donate(lotId: string): Promise<void>;
  setFocus(buildingId: string): Promise<void>;
  claimGift(id: string): Promise<boolean>;
  refreshDiplomacy(): Promise<void>;
  refreshDuel(): Promise<void>;
  /** Diplomacy actions; each refreshes the overview and reports through toasts. */
  diplomacyAct: {
    propose(allianceId: string, kind: ProposalKind): Promise<boolean>;
    respond(id: string, accept: boolean): Promise<boolean>;
    hostile(allianceId: string): Promise<boolean>;
    neutral(allianceId: string): Promise<boolean>;
    war(allianceId: string): Promise<boolean>;
    surrender(allianceId: string): Promise<boolean>;
  };
  run<T>(op: () => Promise<T>, okText?: string): Promise<T | null>;
}

/** The hub's nav badge: pending applications for officers, invites and helpable members for everyone. */
export function allianceBadge(s: Pick<AllianceStore, "alliance" | "requests" | "help" | "gifts"> & { diplomacy?: DiplomacyOverview | null }): number {
  const requests = (s.alliance?.openRequests ?? 0) + (s.requests?.invites.length ?? 0);
  const help = s.help?.filter((h) => h.canHelp).length ?? 0;
  const gifts = s.gifts?.claimable ?? 0;
  const proposals = s.diplomacy?.canAct ? s.diplomacy.proposals.incoming.length : 0;
  return requests + help + gifts + proposals;
}

export const useAlliance = create<AllianceStore>((set, get) => ({
  alliance: undefined,
  list: null,
  requests: null,
  help: null,
  log: null,
  buildings: null,
  gifts: null,
  diplomacy: null,
  duel: null,
  loading: false,
  refresh: async () => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    set({ loading: true });
    try {
      const alliance = await allianceApi.mine();
      set({ alliance, loading: false });
      if (alliance) await Promise.all([get().refreshHelp(), get().refreshRequests(), get().refreshGifts(), get().refreshDiplomacy(), get().refreshDuel()]);
      else set({ buildings: null, gifts: null, diplomacy: null, duel: null });
    } catch (e) {
      set({ loading: false });
      if (set.current()) toast.error(errorText(e));
    }
  },
  search: async (query) => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    try {
      set({ list: await allianceApi.list(query) });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshRequests: async () => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    try {
      set({ requests: await allianceApi.requests() });
    } catch {
      /* logged out */
    }
  },
  refreshHelp: async () => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    try {
      set({ help: await allianceApi.help() });
    } catch {
      /* logged out */
    }
  },
  refreshLog: async () => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    try {
      set({ log: await allianceApi.log() });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  create: async (input) => {
    const alliance = await get().run(() => allianceApi.create(input), t("alliance.toast.created"));
    if (!alliance) return false;
    set({ alliance });
    await get().refreshHelp();
    return true;
  },
  join: async (id, message = "") => {
    const res = await get().run(() => allianceApi.join(id, message));
    if (!res) return false;
    toast.success(t(res.joined ? "alliance.toast.joined" : "alliance.toast.applied"));
    await get().refresh();
    await get().search("");
    return res.joined;
  },
  leave: async () => {
    if (await get().run(() => allianceApi.leave(), t("alliance.toast.left"))) set({ alliance: null, help: null, requests: null, log: null, diplomacy: null, duel: null });
  },
  helpAll: async () => {
    const res = await get().run(() => allianceApi.helpAll());
    if (!res) return 0;
    toast.success(t("alliance.toast.helped", { n: res.helped }));
    await get().refreshHelp();
    return res.helped;
  },
  refreshBuildings: async () => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    try {
      set({ buildings: await allianceApi.buildings() });
    } catch {
      /* not a member */
    }
  },
  refreshGifts: async () => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    try {
      set({ gifts: await allianceApi.gifts() });
    } catch {
      /* not a member */
    }
  },
  donate: async (lotId) => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    const res = await get().run(() => allianceApi.donate(lotId));
    if (!res) return;
    toast.success(t("alliance.contribute.done", { xp: res.xp, coins: res.coins }));
    set((s) => ({ alliance: s.alliance ? { ...s.alliance, xp: res.allianceXp, level: res.allianceLevel, donationsToday: res.lotsToday } : s.alliance, buildings: res.building && s.buildings ? s.buildings.map((b) => (b.id === res.building!.id ? res.building! : b)) : s.buildings }));
    await useSession.getState().load();
  },
  setFocus: async (buildingId) => {
    const building = await get().run(() => allianceApi.setFocus(buildingId), t("alliance.buildings.focusSet"));
    if (!building) return;
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    set((s) => ({ alliance: s.alliance ? { ...s.alliance, focusBuilding: building.id } : s.alliance, buildings: s.buildings ? s.buildings.map((b) => ({ ...b, focused: b.id === building.id })) : s.buildings }));
  },
  claimGift: async (id) => {
    const reward = await get().run(() => allianceApi.claimGift(id), t("alliance.gifts.claimedToast"));
    if (!reward) return false;
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    set((s) => ({ gifts: s.gifts ? { open: s.gifts.open.map((g) => (g.id === id ? { ...g, claimed: true } : g)), claimable: Math.max(0, s.gifts.claimable - 1) } : s.gifts }));
    await useSession.getState().load();
    return true;
  },
  refreshDiplomacy: async () => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    try {
      set({ diplomacy: await allianceApi.diplomacy() });
    } catch {
      /* not a member */
    }
  },
  refreshDuel: async () => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    try {
      set({ duel: await allianceApi.events() });
    } catch {
      /* not a member */
    }
  },
  diplomacyAct: {
    propose: async (allianceId, kind) => {
      const ok = await get().run(() => allianceApi.propose(allianceId, kind), t(kind === "peace" ? "alliance.diplomacy.toast.peaceProposed" : "alliance.diplomacy.toast.proposed"));
      if (ok) await get().refreshDiplomacy();
      return !!ok;
    },
    respond: async (id, accept) => {
      const ok = await get().run(() => allianceApi.respond(id, accept), t(accept ? "alliance.diplomacy.toast.accepted" : "alliance.diplomacy.toast.declined"));
      if (ok) await get().refreshDiplomacy();
      return !!ok;
    },
    hostile: async (allianceId) => {
      const ok = await get().run(() => allianceApi.hostile(allianceId), t("alliance.diplomacy.toast.hostile"));
      if (ok) await get().refreshDiplomacy();
      return !!ok;
    },
    neutral: async (allianceId) => {
      const ok = await get().run(() => allianceApi.neutral(allianceId), t("alliance.diplomacy.toast.neutral"));
      if (ok) await get().refreshDiplomacy();
      return !!ok;
    },
    war: async (allianceId) => {
      const ok = await get().run(() => allianceApi.war(allianceId), t("alliance.diplomacy.toast.war"));
      if (ok) await get().refreshDiplomacy();
      return !!ok;
    },
    surrender: async (allianceId) => {
      const ok = await get().run(() => allianceApi.surrender(allianceId), t("alliance.diplomacy.toast.surrendered"));
      if (ok) await get().refreshDiplomacy();
      return !!ok;
    },
  },
  run: async (op, okText) => {
    const set = scopedSetter<AllianceStore>(useAlliance.setState);
    try {
      const result = await op();
      if (!set.current()) return null;
      await useUnits.getState().refreshMe();
      if (!set.current()) return null;
      if (okText) toast.success(okText);
      return result;
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
      return null;
    }
  },
}));
